import { PortfolioModel } from "../../features/portfolio-info/types/PortfolioModel";
import { AssetType } from "../types/AssetType";
import { dateTimeConversionService } from "./DateTimeConversionService";

type PerformancePosition = {
    type: AssetType;
    volume: number;
    currentSize: number;
};

export type PortfolioPerformanceContext = {
    investedCapital: number;
    lifecycleInDays: number;
};

export type PortfolioPerformance = {
    roi: number | null;
    annualisedRoi: number | null;
};

const millisecondsPerDay = 1000 * 60 * 60 * 24;

class PortfolioPerformanceService {
    createContext(
        positions: PerformancePosition[],
        portfolio: PortfolioModel,
    ): PortfolioPerformanceContext {
        const investedCapital = positions.reduce((sum, position) => {
            switch (position.type) {
                case AssetType.Spot:
                case AssetType.Call:
                case AssetType.Put:
                    return sum + position.volume;

                case AssetType.Futures:
                    return sum - portfolio.marginBuffer * Math.abs(position.currentSize);

                default:
                    return sum;
            }
        }, 0);

        const valuationDate = portfolio.useValuationDate
            ? new Date(portfolio.valuationDate)
            : dateTimeConversionService.getCurrentUtcDate();
        const lifecycleInDays =
            (valuationDate.getTime() - new Date(portfolio.inceptionDate).getTime()) / millisecondsPerDay;

        return {
            investedCapital,
            lifecycleInDays,
        };
    }

    calculate(
        balance: number,
        context: PortfolioPerformanceContext,
    ): PortfolioPerformance {
        const roi = context.investedCapital !== 0
            ? Math.sign(balance) * Math.abs(balance / context.investedCapital * 100)
            : null;
        const annualisedRoi = roi !== null && context.lifecycleInDays > 0
            ? roi / context.lifecycleInDays * 365
            : null;

        return {
            roi: Number.isFinite(roi) ? roi : null,
            annualisedRoi: Number.isFinite(annualisedRoi) ? annualisedRoi : null,
        };
    }

    formatPercentage(value: number | null, alwaysUseSign: boolean = false): string {
        if (value === null || !Number.isFinite(value)) {
            return "N/A";
        }

        const formattedValue = Math.abs(value) < 10
            ? value.toFixed(1)
            : value.toFixed(0);

        return alwaysUseSign && value > 0
            ? `+${formattedValue}%`
            : `${formattedValue}%`;
    }

    formatMetric(
        balance: number,
        context: PortfolioPerformanceContext,
        useAnnualisedRoi: boolean,
        alwaysUseSign: boolean = false,
    ): string {
        const { roi, annualisedRoi } = this.calculate(balance, context);

        return this.formatPercentage(useAnnualisedRoi ? annualisedRoi : roi, alwaysUseSign);
    }
}

export const portfolioPerformanceService = new PortfolioPerformanceService();
